import { useMemo, useState } from "react";

type GuideCard = {
  title: string;
  description: string;
  path: string;
  steps: string[];
};

type FaqItem = {
  question: string;
  answer: string;
  tags: string[];
};

const guides: GuideCard[] = [
  {
    title: "처음 시작하기",
    description: "학교 작업공간을 선택하고 오늘 처리할 업무부터 확인합니다.",
    path: "/",
    steps: ["학교 작업공간 확인", "홈의 미처리 업무 확인", "학생 검색", "업무함에서 우선순위 확인"]
  },
  {
    title: "학생 · 학적관리",
    description: "Student Master를 기준으로 학생, 학년·반, 전학·진급 이력을 관리합니다.",
    path: "/learners",
    steps: ["학생 검색", "Student 360 확인", "학적 이력 확인", "필요한 다음 업무 연결"]
  },
  {
    title: "진로검사 보내기",
    description: "검사를 고른 뒤 학년·반·학생을 선택해 일괄 배정합니다.",
    path: "/assessments",
    steps: ["검사 선택", "대상 필터", "학생 선택", "배정 후 진행상태 확인"]
  },
  {
    title: "수업 · 활동 운영",
    description: "계획 → 프로그램 → 활동 → 세션 → 참여 흐름으로 운영합니다.",
    path: "/programs",
    steps: ["교육계획 선택", "프로그램 생성/선택", "활동·세션 설정", "학생 참여 등록"]
  },
  {
    title: "상담 · Case 관리",
    description: "상담 기록과 Student Career Case를 연결해 학생 지원 흐름을 이어갑니다.",
    path: "/caseload",
    steps: ["학생 또는 Case 선택", "목표·지원계획 확인", "실행업무 연결", "성과점검·후속관리"]
  },
  {
    title: "디지털배지",
    description: "검증된 근거를 바탕으로 배지를 부여하고 발급·수령 상태를 관리합니다.",
    path: "/badges",
    steps: ["Evidence 확인", "평가·성취 확인", "배지 부여", "발급대장·수령상태 확인"]
  },
  {
    title: "체험처 · 파트너",
    description: "외부기관 발굴부터 일정 확약, 학생 배치, 후속상담까지 관리합니다.",
    path: "/partners",
    steps: ["파트너 등록", "학교 승인", "체험 일정 확약", "학생 배치·후속업무"]
  },
  {
    title: "리포트 · 제출",
    description: "학교 운영 데이터를 확인하고 공식 보고서와 부속자료를 준비합니다.",
    path: "/reports",
    steps: ["운영 데이터 점검", "학년·학급 비교", "배지·프로그램 통계", "공식 제출상태 확인"]
  }
];

const faqs: FaqItem[] = [
  {
    question: "학생이 검색되지 않아요.",
    answer: "현재 선택한 학교 작업공간과 학생의 재학 상태를 먼저 확인하세요. 전학·졸업·학적이동 학생은 학적관리에서 이력을 확인할 수 있습니다.",
    tags: ["학생", "학적", "검색"]
  },
  {
    question: "다른 학교의 학생 기록이 보이지 않는 이유는 무엇인가요?",
    answer: "센터는 학교 Workspace와 학생별 접근권한을 함께 확인합니다. Case 공동담당이나 인수인계만으로 학생 접근권한이 자동 부여되지는 않습니다.",
    tags: ["권한", "보안", "학생"]
  },
  {
    question: "검사를 학생에게 다시 보낼 수 있나요?",
    answer: "진로검사 화면에서 현재 진행상태를 확인한 뒤 대상 학생을 다시 선택하세요. 이미 진행 중인 배정은 중복되지 않도록 서버에서 상태를 확인합니다.",
    tags: ["검사", "배정"]
  },
  {
    question: "상담기록과 Career Case는 같은 건가요?",
    answer: "아닙니다. 상담은 개별 상담 세션의 기록이고 Career Case는 목표·지원계획·실행·성과점검을 묶는 장기 학생지원 흐름입니다.",
    tags: ["상담", "Case"]
  },
  {
    question: "배지를 바로 발급할 수 있나요?",
    answer: "배지는 활동 근거와 검증 상태에 따라 흐름이 달라집니다. Evidence → 평가·성취 → 배지 부여 → 발급대장 순서로 확인하는 것을 권장합니다.",
    tags: ["배지", "Evidence", "성취"]
  },
  {
    question: "전학하면 기존 배지는 사라지나요?",
    answer: "아니요. Student Master의 연속성을 유지하므로 전학 전 진로기록과 디지털배지 이력을 계속 보존하도록 설계되어 있습니다.",
    tags: ["전학", "배지", "학생"]
  },
  {
    question: "보호자 동의 없이 외부기관에 학생정보를 공유할 수 있나요?",
    answer: "동의·공유 기능은 목적, 수신자, 범위, 유효기간을 먼저 고정하고 유효한 동의 범위 안에서 최소정보만 공유하도록 설계되어 있습니다.",
    tags: ["동의", "공유", "개인정보"]
  },
  {
    question: "체험처 담당자에게 학생 명단이 그대로 보이나요?",
    answer: "체험처 확약 공개 화면에는 일정·장소·요청 정원 중심 정보만 제공하며 학생 명단은 노출하지 않도록 구성되어 있습니다.",
    tags: ["체험처", "개인정보"]
  },
  {
    question: "업무함에 표시되는 항목은 어떻게 생기나요?",
    answer: "상담 후속일정, 학적이동, 실패한 일괄작업, Evidence 검토, 배지 수령문제, Case 인수인계 등 실제 운영 상태에서 파생됩니다.",
    tags: ["업무함", "알림"]
  },
  {
    question: "리포트 숫자가 예상과 다르면 무엇부터 확인하나요?",
    answer: "학년도, 학급배정, 프로그램 상태, 참여완료, 배지 상태를 순서대로 확인하세요. 리포트 화면의 데이터 점검 항목도 함께 확인하면 좋습니다.",
    tags: ["리포트", "통계"]
  }
];

function navigate(path: string) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function CenterHelpPage() {
  const [query, setQuery] = useState("");

  const filteredFaqs = useMemo(() => {
    const value = query.trim().toLowerCase();
    if (!value) return faqs;
    return faqs.filter((item) =>
      [item.question, item.answer, ...item.tags].some((text) => text.toLowerCase().includes(value))
    );
  }, [query]);

  return (
    <section className="center-help-page">
      <header className="center-help-hero">
        <div>
          <p className="eyebrow">CENTER HELP</p>
          <h2>사용안내 · Q&amp;A</h2>
          <p>진로교사가 필요한 기능을 빠르게 찾고, 막히는 지점을 스스로 해결할 수 있도록 정리했습니다.</p>
        </div>
        <div className="center-help-search">
          <label htmlFor="center-help-search">무엇이 궁금한가요?</label>
          <input
            id="center-help-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="예: 전학, 배지, 보호자 동의, 리포트"
          />
        </div>
      </header>

      <div className="center-help-section-heading">
        <div>
          <span>01</span>
          <div>
            <h3>빠른 사용안내</h3>
            <p>업무 목적을 선택하면 해당 메뉴로 바로 이동합니다.</p>
          </div>
        </div>
      </div>

      <div className="center-help-guide-grid">
        {guides.map((guide) => (
          <article className="center-help-guide-card" key={guide.title}>
            <div>
              <h4>{guide.title}</h4>
              <p>{guide.description}</p>
            </div>
            <ol>
              {guide.steps.map((step) => <li key={step}>{step}</li>)}
            </ol>
            <button onClick={() => navigate(guide.path)}>이 메뉴 열기 →</button>
          </article>
        ))}
      </div>

      <div className="center-help-two-column">
        <section>
          <div className="center-help-section-heading compact">
            <div>
              <span>02</span>
              <div>
                <h3>자주 묻는 질문</h3>
                <p>{query ? `“${query}” 검색 결과 ${filteredFaqs.length}건` : "교사 사용 중 자주 생기는 질문을 모았습니다."}</p>
              </div>
            </div>
          </div>

          <div className="center-help-faq-list">
            {filteredFaqs.length ? filteredFaqs.map((item) => (
              <details key={item.question}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
                <div className="center-help-tags">
                  {item.tags.map((tag) => <span key={tag}>{tag}</span>)}
                </div>
              </details>
            )) : (
              <div className="center-help-empty">검색되는 질문이 없습니다. 다른 단어로 검색해 보세요.</div>
            )}
          </div>
        </section>

        <aside className="center-help-side">
          <section className="center-help-card">
            <span className="center-help-card-icon">!</span>
            <h3>문제가 생겼나요?</h3>
            <ol>
              <li>현재 선택한 학교 작업공간을 확인합니다.</li>
              <li>페이지를 새로고침한 뒤 같은 작업을 다시 시도합니다.</li>
              <li>업무함과 알림에서 실패·대기 상태를 확인합니다.</li>
              <li>계속되면 오류문구와 발생시각을 함께 전달합니다.</li>
            </ol>
          </section>

          <section className="center-help-card accent">
            <span className="center-help-card-icon">?</span>
            <h3>문의할 때 함께 알려주세요</h3>
            <ul>
              <li>학교명 / 작업공간</li>
              <li>사용 중인 메뉴명</li>
              <li>학생 이름 대신 학생을 식별할 수 있는 내부 정보</li>
              <li>발생 시각과 오류문구</li>
              <li>가능하면 화면 캡처</li>
            </ul>
            <p className="center-help-privacy-note">민감정보·비밀번호·Google 로그인 정보는 문의 내용에 포함하지 마세요.</p>
          </section>

          <section className="center-help-card">
            <span className="center-help-card-icon">↗</span>
            <h3>역할별 기본 흐름</h3>
            <dl className="center-help-role-list">
              <div><dt>진로교사</dt><dd>학생 → 검사·상담·활동 → Case → Evidence → 배지</dd></div>
              <div><dt>학교관리자</dt><dd>교사 업무 + 학교 일괄업무·승인·학년도 마감</dd></div>
              <div><dt>보호자</dt><dd>공개 링크에서 동의·철회</dd></div>
              <div><dt>체험처</dt><dd>공개 링크에서 일정·정원 확약</dd></div>
            </dl>
          </section>
        </aside>
      </div>
    </section>
  );
}
